import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { Button } from '../Button/Button'
import { useIsDark } from '../Workbench/useIsDark'
import { useTargetRects, type Rect } from './useTargetRects'

/**
 * Non-blocking spotlight coach mark: dims the screen around one target, rings
 * it and anchors a small copy card beside it — while every click still lands
 * on the app underneath (only the card itself is interactive). Counterpart to
 * the blocking OnboardingTour: that one narrates a read-only walkthrough; this
 * one points at something the user is free to use immediately. Renders nothing
 * until the target exists, so it can sit mounted "pending" before its anchor
 * appears (e.g. a chat card in a collapsed panel).
 */

const PADDING = 6
const HOLE_RADIUS = 8
const RING_PADDING = 1
const RING_RADIUS = 6
const TOOLTIP_GAP = 12
const TOOLTIP_WIDTH = 280
// Rough card height for viewport clamping (copy + footer); exactness doesn't
// matter, it only keeps the card from hugging the screen edge.
const EST_CARD_H = 120

export type CoachMarkPlacement = 'bottom' | 'left' | 'top' | 'right'

export interface CoachMarkProps {
  targetSelector: string
  copy: string
  placement: CoachMarkPlacement
  primaryLabel: string
  onPrimary: () => void
  /** X button and Esc. */
  onDismiss: () => void
}

export function CoachMark({
  targetSelector,
  copy,
  placement,
  primaryLabel,
  onPrimary,
  onDismiss,
}: CoachMarkProps) {
  const rects = useTargetRects([targetSelector])
  const isDark = useIsDark()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onDismiss])

  // Target not mounted (yet) — stay invisible until it appears.
  if (!rects || rects.length === 0) return null

  const dim = isDark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.6)'
  const r = rects[0]
  const hole: Rect = {
    top: r.top - PADDING,
    left: r.left - PADDING,
    width: r.width + PADDING * 2,
    height: r.height + PADDING * 2,
  }

  // Side placements center on the hole; clamp so the card can't slide off
  // the viewport edges.
  const sideTop = Math.min(
    Math.max(8, hole.top + hole.height / 2 - 56),
    window.innerHeight - EST_CARD_H - 8,
  )
  let tipStyle: React.CSSProperties
  let arrowStyle: React.CSSProperties
  if (placement === 'bottom') {
    tipStyle = {
      top: hole.top + hole.height + TOOLTIP_GAP,
      left: Math.max(8, Math.min(hole.left + hole.width / 2 - TOOLTIP_WIDTH / 2, window.innerWidth - TOOLTIP_WIDTH - 8)),
      width: TOOLTIP_WIDTH,
    }
    arrowStyle = {
      top: -6,
      left: hole.left + hole.width / 2 - (tipStyle.left as number) - 6,
      borderLeft: '1px solid var(--border)',
      borderTop: '1px solid var(--border)',
    }
  } else if (placement === 'top') {
    tipStyle = {
      top: hole.top - TOOLTIP_GAP,
      left: Math.max(8, Math.min(hole.left + hole.width / 2 - TOOLTIP_WIDTH / 2, window.innerWidth - TOOLTIP_WIDTH - 8)),
      width: TOOLTIP_WIDTH,
      transform: 'translateY(-100%)',
    }
    arrowStyle = {
      bottom: -6,
      left: hole.left + hole.width / 2 - (tipStyle.left as number) - 6,
      borderRight: '1px solid var(--border)',
      borderBottom: '1px solid var(--border)',
    }
  } else if (placement === 'right') {
    tipStyle = {
      top: sideTop,
      left: Math.max(8, Math.min(hole.left + hole.width + TOOLTIP_GAP, window.innerWidth - TOOLTIP_WIDTH - 8)),
      width: TOOLTIP_WIDTH,
    }
    arrowStyle = {
      left: -6,
      top: Math.max(10, hole.top + hole.height / 2 - sideTop - 6),
      borderLeft: '1px solid var(--border)',
      borderBottom: '1px solid var(--border)',
    }
  } else {
    // left of the target
    tipStyle = {
      top: sideTop,
      left: Math.max(8, hole.left - TOOLTIP_GAP - TOOLTIP_WIDTH),
      width: TOOLTIP_WIDTH,
    }
    arrowStyle = {
      right: -6,
      top: Math.max(10, hole.top + hole.height / 2 - sideTop - 6),
      borderRight: '1px solid var(--border)',
      borderTop: '1px solid var(--border)',
    }
  }

  return createPortal(
    // Portal lands on <body>, outside #root — re-scope the design tokens with
    // .v1, but kill its opaque background-color (tokens.css paints .v1 with
    // var(--background), which would black out the whole viewport here).
    // pointer-events-none on the root is the whole "non-blocking" mechanism:
    // the scrim is visual only; just the card catches input.
    //
    // z-[140] is above the chat sheet (BottomSheet z-[130]/[135]) BY REQUIREMENT:
    // BacktestCoach's first step spotlights a chat message, which on mobile lives
    // inside that sheet. Under it, the dim and the ring would paint beneath an
    // opaque panel and — since hit-testing follows paint order — the card would be
    // unreachable, stranding a step that has no grace timeout.
    <div
      className="v1 fixed inset-0 z-[140] pointer-events-none"
      style={{ backgroundColor: 'transparent' }}
      role="dialog"
      aria-label={copy}
    >
      {/* Spotlight: the giant shadow spread paints the dim everywhere except
          the (genuinely rounded) hole over the target. */}
      <div
        className="absolute"
        style={{ ...hole, borderRadius: HOLE_RADIUS, boxShadow: `0 0 0 200vmax ${dim}` }}
      />
      {/* Highlight ring. */}
      <div
        className="absolute"
        style={{
          top: r.top - RING_PADDING,
          left: r.left - RING_PADDING,
          width: r.width + RING_PADDING * 2,
          height: r.height + RING_PADDING * 2,
          borderRadius: RING_RADIUS,
          boxShadow: '0 0 0 2px var(--primary)',
        }}
      />
      {/* Copy card. */}
      <div
        className="absolute pointer-events-auto rounded-md border border-border bg-card shadow-popover px-4 py-3"
        style={tipStyle}
      >
        <div className="absolute h-3 w-3 bg-card" style={{ ...arrowStyle, transform: 'rotate(45deg)' }} />
        <div className="relative flex items-start gap-2">
          <p className="flex-1 text-body text-foreground leading-relaxed" style={{ fontSize: 12 }}>
            {copy}
          </p>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={onDismiss}
            className="text-muted-foreground hover:text-foreground cursor-pointer shrink-0 -mt-0.5"
          >
            <X size={12} />
          </button>
        </div>
        <div className="relative mt-2.5 flex justify-end">
          <Button type="button" size="xs" onClick={onPrimary}>
            {primaryLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
